import { Button, Checkbox, Input, Modal, Tooltip } from "antd";
import { groupBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsFillSendFill } from "react-icons/bs";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../api";
import { conversationType } from "../../config/Constant";
import { useMenuContext } from "../../context/MenuContext";
import AddMemberList from "../../pages/conversation/mail/AddMemberList";
import { useInfoUser } from "../../store/UserStore";
import {
  extractEditorResourceIds,
  getFirstLineFromHtml,
  getPlainTextFromHtml,
  openNotificationError,
} from "../../utils/Utils";
import FileHandlerWrapper from "../mail/FileHandlerWrapper";
import { MailFilesPreview } from "../preview/MailFilesPreview";
import EmailComposer from "../richTextEditor/EmailComposer";
import { AddMemberModal } from "./AddMemberModal";
import { PreviewFileUploadModal } from "./PreviewFileUploadModal";
import "./style.scss";
import { useMailContext } from "../../context/MailContext";
import { RESOURCE } from "../../api/apiConstant";

const ForwardMailModal = ({
  isForwardMail,
  setIsForwardMail,
  notification,
  addMemberOrg,
  cancelForward,
}) => {
  const { languageMap, user } = useInfoUser();
  const { openAddMemberModal, handleChangeConversation } = useMenuContext();

  const { memberOrgList, setMemberOrgList, userList, setIsLoading, isLoading } =
    useMailContext();
  const [uploadFiles, setUploadFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const replyDivRef = useRef(null);
  const [mailContent, setMailContent] = useState({
    content: "",
    oldContent: "",
  });
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [forwardTitle, setForwardTitle] = useState("");
  const [isNotification, setIsNotification] = useState(false);
  const extractUser = useCallback(
    (id) => {
      return userList?.find((s) => s.userId === id);
    },
    [userList],
  );

  const memberGroupList = useMemo(() => {
    return Object.values(
      groupBy(
        memberOrgList?.filter((org) => org?.type === "PERSON" && org?.checked),
        "objectId",
      ),
    );
  }, [memberOrgList]);

  const onCreateNotificationSuccessfully = (conversation) => {
    conversation.type = conversationType.NOTIFICATION;
    handleChangeConversation(conversation);
  };

  const validateSendMail = () => {
    if (
      mailContent?.content?.trim()?.length === 0 &&
      previewFiles?.length === 0
    ) {
      openNotificationError(
        languageMap?.["error.mailHaveContent"] ??
          "You have to input email content",
      );

      setIsLoading(false);
      return false;
    }

    const { userIds = [], ccIds = [] } = addMemberOrg ? addMemberOrg() : {};

    if (userIds?.length === 0 && ccIds?.length === 0) {
      openNotificationError(
        languageMap?.["error.mailHaveRecipient"] ??
          "You have to select recipient or cc",
      );

      setIsLoading(false);
      return false;
    }

    return true;
  };

  const createNotification = async () => {
    if (!validateSendMail()) return;

    setIsLoading(true);
    const { userIds = [], ccIds = [] } = addMemberOrg ? addMemberOrg() : {};

    const conversationResponse =
      await apiFactory.conversationApi.createEmptyConversation({
        conversationName:
          forwardTitle || getFirstLineFromHtml(mailContent?.content),
        isNotification: isNotification,
      });

    if (conversationResponse?.status !== 200) {
      openNotificationError("Create conversation failed");
      setIsLoading(false);
      return;
    }

    const letter = await apiFactory.letterApi.send({
      requestUuid: uuidv4(),
      forwardLetterId: notification?.letterId,
      // name: title || getFirstLineFromHtml(mailContent?.content),
      recipientIds: userIds,
      ccIds,
      conversationType: conversationType.NOTIFICATION,
      conversationId: conversationResponse?.data?.conversationId,
      isNotification: isNotification,
      plainText: getPlainTextFromHtml(mailContent?.content),

      content: processHtmlForSaving(mailContent?.content),
      isDraft: false,
      files: previewFiles || [],
      updatedMediaConversationId: true,
      draftMailId: notification?.draftMail?.draftMailId,
      mailId: notification?.mailId,
      editorResourceId: extractEditorResourceIds(mailContent?.content),
      isForwardMail: false,
      isForward: true,
    });

    onCreateNotificationSuccessfully({
      conversationId: conversationResponse?.data?.conversationId,
    });
    setIsLoading(false);
    setIsForwardMail(false);
  };

  const onKeyDown = (e) => {
    if (e.isComposing || e.keyCode === 229 || isLoading) return;
    // Ctrl + Enter
    if ((e.ctrlKey || e.metaKey) && (e.key === "Enter" || e.keyCode === 13)) {
      e.preventDefault();
      createNotification();
    }
  };

  const processHtmlForSaving = (htmlContent) => {
    if (!htmlContent) return "";

    const doc = new DOMParser().parseFromString(htmlContent, "text/html");
    const images = Array.from(doc.querySelectorAll("img"));

    images.forEach((imgDoc) => {
      const resourceId = imgDoc.getAttribute("data-resource-id");

      if (resourceId) {
        const apiUrl = `${process.env.REACT_APP_AUTHENTICATION_API_URL}/${RESOURCE}/free/file/${resourceId}`;

        imgDoc.src = apiUrl;

        imgDoc.removeAttribute("data-resource-id");
      }
    });

    return doc.body.innerHTML;
  };

  const handleCancelUpload = () => {
    setIsOpenUploadModal(false);
    setUploadFiles([]);
  };

  useEffect(() => {
    setPreviewFiles(notification?.files);
  }, [notification]);

  const customHeader = (
    <div className="custom-modal-header flex items-center justify-between pt-5">
      <span className="text-lg font-bold">
        {languageMap?.["chat.mail.title.forward"] ?? "Forward Mail"}
      </span>
      <Tooltip
        placement="bottom"
        title={
          languageMap?.["modal.notification.notice"] ??
          "When you want to notify to a lot of people"
        }
        color={"#0091ff"}
      >
        <Checkbox
          checked={isNotification}
          onChange={() => setIsNotification(!isNotification)}
          className="font-semibold whitespace-nowrap"
        >
          {languageMap?.["notification.notice"] ?? "Notice"}
        </Checkbox>
      </Tooltip>
    </div>
  );

  return (
    <Modal
      title={customHeader}
      closable={{ "aria-label": "Custom Close Button" }}
      open={isForwardMail}
      width={"80vw"}
      onOk={() => setIsForwardMail(false)}
      onCancel={cancelForward}
      centered
      footer={false}
    >
      <div onKeyDown={onKeyDown}>
        <div className="card-detail">
          <AddMemberList
            title={languageMap?.["chat.mail.recipient"] ?? "Recipient"}
            onClick={openAddMemberModal}
            memberGroupList={memberGroupList}
            orgList={memberOrgList}
            setOrgList={setMemberOrgList}
          />
        </div>
        <div className="flex justify-between mt-[10px] w-full">
          <div className="w-[25%]">
            <AddMemberModal
              isModalOpen={true}
              screen={conversationType.CREATE_NOTIFICATION}
              orgList={memberOrgList}
              setOrgList={setMemberOrgList}
              isForwardMail={isForwardMail}
            />
          </div>
          <div className="w-[75%]">
            <div className="flex flex-col gap-2 w-full py-1 border border-gray-300 transition-all">
              <Input
                className="border rounded p-1 px-3 w-full"
                placeholder={
                  languageMap?.["modal.notification.title"] ?? "Title(not blank)"
                }
                maxLength={255}
                variant="borderless"
                value={forwardTitle}
                onChange={(e) => {
                  setForwardTitle(e.target.value);
                }}
                allowClear
              />
            </div>

            <FileHandlerWrapper
              setUploadFiles={setUploadFiles}
              setIsOpenUploadModal={setIsOpenUploadModal}
              languageMap={languageMap}
              innerRef={replyDivRef}
            >
              <div className="w-full h-[450px] bg-white rounded-[10px] mb-1">
                <div className="border-black border-radius pt-2 pb-2">
                  <EmailComposer
                    originalContent={notification?.content}
                    notification={notification}
                    setMailContent={setMailContent}
                    mailContent={mailContent}
                    conversationId={notification?.conversationId}
                    isForwardMail={isForwardMail}
                    sender={extractUser(notification?.senderId)}
                  />
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex gap-[10px]">
                  <MailFilesPreview
                    previewFiles={previewFiles}
                    setPreviewFiles={setPreviewFiles}
                    setIsOpenUploadModal={setIsOpenUploadModal}
                    setUploadFiles={setUploadFiles}
                  />
                </div>
                <div className="flex justify-end mt-[20px]">
                  <Button
                    className="bg-[#2a56b9] text-[white]"
                    onClick={createNotification}
                    disabled={isLoading}
                    icon={
                      <BsFillSendFill className="text-[18px]" color="#30a3e6" />
                    }
                  >
                    {languageMap?.["chat.mail.btn.forward"] ?? "Forward"}
                  </Button>
                </div>
              </div>
            </FileHandlerWrapper>
          </div>
        </div>
        {isOpenUploadModal && (
          <PreviewFileUploadModal
            isModalOpen={isOpenUploadModal}
            handleCancel={handleCancelUpload}
            uploadFiles={uploadFiles}
            setUploadFiles={setUploadFiles}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
          />
        )}
      </div>
    </Modal>
  );
};

export { ForwardMailModal };
